// src/components/layout/TopHeader.tsx
// Renamed from TopNav.tsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import './TopHeader.scss'; // Rename SCSS file too
import { AppUser, UserRole } from '../../types'; // Adjust path
// Import icons if needed
// import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
// import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
// Removed Feedback icon import
import { Bell, Bug, CircleUser, Flame, HelpCircle, LogOut, Menu, Settings2, UserCircle, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Driver, DriveStep } from 'driver.js';
import FeedbackModal from '../feedback/FeedbackModal';
import { useAuth } from '../../contexts/AuthContext';
import { useRolePlay } from '../../contexts/RolePlayContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { Notification } from '../../services/notificationService';
import { fetchMyGamification } from '../../services/gamificationApi';
import BadgeUnlockPopup from '../gamification/BadgeUnlockPopup';
import { useTutorial } from '../tutorial/useTutorial';
import DemoProxySwitcher from './DemoProxySwitcher';
import { getReferenceSteps } from '../tutorial/referenceSteps';
import { getPageSteps } from '../tutorial/pageSteps';

// Routes gated behind ProtectedRoute's/RoleProtectedRoute's `blockTrial` in App.tsx
// (mirrors backend/app/communication/constants.py's route set) — a trial user
// hitting one of these gets silently bounced to "/", so a notification link
// pointing here is suppressed for the current viewer instead of followed.
const TRIAL_BLOCKED_ROUTES = new Set(['/my-progress', '/competitions']);

interface TopHeaderProps {
    currentUser: AppUser | null;
    onLogout: () => Promise<void>;
    // Removed onToggleFeedback
    // Mobile off-canvas sidebar drawer (see Layout.tsx) — the button below is
    // only shown ≤768px via CSS, and hidden entirely when there's no sidebar.
    isMobileSidebarOpen: boolean;
    onToggleMobileSidebar: () => void;
    hideSidebarToggle?: boolean;
}

const getRoleLabel = (role?: UserRole) => {
    switch (role) {
        case "super_admin": return "Super Admin";
        case "admin": return "Administrator";
        case "trainer": return "Trainer";
        case "trainee": return "Trainee";
        default: return "User";
    }
};

const getBreadcrumbItems = (pathname: string) => {
    const pathParts = pathname.split('/').filter(Boolean);

    // "/" is the Home dashboard (Role Play lives at "/roleplay")
    if (pathParts.length === 0) {
        return [
            { label: 'Home', path: '/' }
        ];
    }

    const items = [];
    let currentPath = '';

    // UUID pattern — skip these segments (detail pages handle their own breadcrumb)
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    for (const part of pathParts) {
        currentPath += `/${part}`;

        // Skip UUID segments — detail pages show their own context
        if (uuidPattern.test(part)) continue;

        // Default: kebab-case segment → Title Case ("my-progress" → "My Progress")
        let label = part
            .split('-')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

        // The only segment the Title Case default gets wrong: "roleplay" is one
        // word in the path but two in the product's name. ("ftue" used to be here
        // too — there is no /ftue route, the FTUE flow lives at /getting-started,
        // which the default already renders correctly.)
        switch (part) {
            case 'roleplay':
                label = 'Role Play';
                break;
        }

        items.push({ label, path: currentPath });
    }

    return items;
};

const formatNotificationTime = (createdAt: string): string => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffMs = now.getTime() - created.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHrs = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMin < 60) return `${Math.max(1, diffMin)}m ago`;
    if (diffHrs < 24) return `${diffHrs}h ago`;
    if (diffDays < 7) return created.toLocaleDateString('en-US', { weekday: 'short' });
    return created.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
};

const TopHeader: React.FC<TopHeaderProps> = ({
    currentUser,
    onLogout,
    isMobileSidebarOpen,
    onToggleMobileSidebar,
    hideSidebarToggle = false,
}) => {
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState<boolean>(false);
    const [isNotifOpen, setIsNotifOpen] = useState<boolean>(false);
    const [isTutorialMenuOpen, setIsTutorialMenuOpen] = useState<boolean>(false);
    const [streakCount, setStreakCount] = useState<number | null>(null);
    const [badgeQueue, setBadgeQueue] = useState<{ id: string; key: string; name: string }[]>([]);
    const knownBadgeKeysRef = useRef<Set<string> | null>(null);
    const { token, isTrialUser } = useAuth();
    const menuRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const notifRef = useRef<HTMLDivElement>(null);
    const notifButtonRef = useRef<HTMLButtonElement>(null);
    const tutorialMenuRef = useRef<HTMLDivElement>(null);
    const tutorialButtonRef = useRef<HTMLButtonElement>(null);
    const pageTourDriverRef = useRef<Driver | null>(null);
    const location = useLocation();
    const navigate = useNavigate();
    const breadcrumbItems = getBreadcrumbItems(location.pathname);
    const { isMarketplaceUser, userRole, realRole, isProxyActive } = useAuth();
    // The "My Profile" entry surfaces the marketplace billing/subscription view, so
    // only the purchaser (trainer) or an admin of a marketplace org sees it; manual/
    // Stripe orgs reach their own profile page through their own layout.
    const canSeeSubscription = isMarketplaceUser && (userRole === 'trainer' || userRole === 'admin');
    const { rolePlaySelections, componentDetails } = useRolePlay();
    const { startReferenceTour } = useTutorial();
    // Computed fresh each time the dropdown is opened (not memoized on
    // location.pathname) — several pages render their tour-anchor DOM ids
    // asynchronously (after data loads) or via in-page state changes (tab
    // switches, an inline editor opening/closing) with no route change, so a
    // route-keyed memo can freeze on a stale (often empty) snapshot forever.
    const [pageTourSteps, setPageTourSteps] = useState<DriveStep[]>([]);
    const notifListRef = useRef<HTMLDivElement>(null);
    const { notifications, unreadCount, loading, loadingMore, hasMore, loadNotifications, loadMoreNotifications, markAsRead, markAllAsRead } = useNotifications();

    // Close menus if clicked outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                menuRef.current && !menuRef.current.contains(event.target as Node) &&
                buttonRef.current && !buttonRef.current.contains(event.target as Node)
            ) {
                setIsMenuOpen(false);
            }
            if (
                notifRef.current && !notifRef.current.contains(event.target as Node) &&
                notifButtonRef.current && !notifButtonRef.current.contains(event.target as Node)
            ) {
                setIsNotifOpen(false);
            }
            if (
                tutorialMenuRef.current && !tutorialMenuRef.current.contains(event.target as Node) &&
                tutorialButtonRef.current && !tutorialButtonRef.current.contains(event.target as Node)
            ) {
                setIsTutorialMenuOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Escape closes whichever header menu is open and hands focus back to its
    // trigger, so keyboard users aren't left inside a menu they can't dismiss.
    useEffect(() => {
        if (!isMenuOpen && !isNotifOpen && !isTutorialMenuOpen) return;
        function handleEscape(event: KeyboardEvent) {
            if (event.key !== 'Escape' || event.defaultPrevented) return;
            if (isMenuOpen) { setIsMenuOpen(false); buttonRef.current?.focus(); }
            if (isNotifOpen) { setIsNotifOpen(false); notifButtonRef.current?.focus(); }
            if (isTutorialMenuOpen) { setIsTutorialMenuOpen(false); tutorialButtonRef.current?.focus(); }
        }
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isMenuOpen, isNotifOpen, isTutorialMenuOpen]);

    // Refetch gamification state (streak + badges) whenever there's a real reason
    // to expect it changed, instead of a tight fixed-interval poll. /gamification/me
    // is Redis-cached for 120s server-side, so these calls are cheap even when frequent.
    const pollGamification = useCallback(() => {
        if (!token || isTrialUser) return;
        fetchMyGamification(token)
            .then((data) => {
                setStreakCount(data.streak_current);

                const currentKeys = new Set(data.badges.map((b) => b.key));
                if (knownBadgeKeysRef.current) {
                    const newlyEarned = data.badges.filter((b) => !knownBadgeKeysRef.current!.has(b.key));
                    if (newlyEarned.length > 0) {
                        setBadgeQueue((prev) => [
                            ...prev,
                            ...newlyEarned.map((b) => ({
                                id: `${b.key}-${b.awarded_at}`,
                                key: b.key,
                                name: b.name,
                            })),
                        ]);
                    }
                }
                knownBadgeKeysRef.current = currentKeys;
            })
            .catch(() => {/* non-fatal */});
    }, [token, isTrialUser]);

    // Trigger 1: on mount and on every route change (navigating away from an active
    // roleplay session is the most common moment gamification state just changed).
    useEffect(() => {
        pollGamification();
    }, [pollGamification, location.pathname]);

    // Trigger 2: event-driven — tab refocus, an explicit "session finished processing"
    // signal (dispatched by useSessionDetail when feedback_status completes), plus a
    // long-interval fallback in case both of the above are missed on a given page.
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') pollGamification();
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('gamification:refresh', pollGamification);
        const fallbackIntervalId = setInterval(pollGamification, 20000);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('gamification:refresh', pollGamification);
            clearInterval(fallbackIntervalId);
        };
    }, [pollGamification]);

    // Infinite scroll: load next page when user scrolls within 80px of the bottom
    useEffect(() => {
        const listEl = notifListRef.current;
        if (!listEl) return;

        function handleScroll() {
            if (!listEl) return;
            const nearBottom = listEl.scrollHeight - listEl.scrollTop - listEl.clientHeight < 80;
            if (nearBottom && hasMore && !loadingMore) {
                loadMoreNotifications();
            }
        }

        listEl.addEventListener('scroll', handleScroll);
        return () => listEl.removeEventListener('scroll', handleScroll);
    }, [hasMore, loadingMore, loadMoreNotifications, isNotifOpen]);

    const handleBellClick = () => {
        const opening = !isNotifOpen;
        setIsNotifOpen(opening);
        if (opening) {
            // Always reload from page 1 when re-opening so the list is fresh
            loadNotifications();
        }
    };

    const handleNotifClick = (notif: Notification) => {
        if (!notif.read) {
            markAsRead(notif.id);
        }
        // Links are persisted independent of trial status (so they don't go stale
        // once a trial user upgrades) — routes behind ProtectedRoute's blockTrial
        // are gated here instead, against the viewer's *current* trial status.
        if (notif.link && !(isTrialUser && TRIAL_BLOCKED_ROUTES.has(notif.link))) {
            setIsNotifOpen(false);
            navigate(notif.link);
        }
    };

    const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);
    // The visual badge is aria-hidden, so the bell's own name carries the count.
    const bellLabel = unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications';

    return (
        <header className="top-header"> {/* Use new class name */}
            {/* Left side: Mimic Trigger (visual placeholder if needed, non-functional), Separator, Breadcrumb */}
            <div className="top-header-left">
                {/* Mobile off-canvas sidebar toggle — hidden by default via CSS,
                    shown only ≤768px (see TopHeader.scss), and never rendered
                    when the layout has no sidebar at all (e.g. FTUE). */}
                {!hideSidebarToggle && (
                    <button
                        type="button"
                        className="button-ghost header-button sidebar-mobile-toggle"
                        onClick={onToggleMobileSidebar}
                        aria-label={isMobileSidebarOpen ? 'Close menu' : 'Open menu'}
                        aria-expanded={isMobileSidebarOpen}
                    >
                        {isMobileSidebarOpen ? <X /> : <Menu />}
                    </button>
                )}
                 {/* Visual Separator */}
                 {/* <div className="separator-vertical"></div> */}

                {/* Static Breadcrumb Example */}
                <nav className="breadcrumb" aria-label="breadcrumb">
                    <ol className="breadcrumb-list">
                        {breadcrumbItems.map((item, index) => (
                            <React.Fragment key={item.path}>
                                <li className={`breadcrumb-item ${index === breadcrumbItems.length - 1 ? 'current' : ''}`}>
                                    {index === breadcrumbItems.length - 1 ? (
                                        item.label
                                    ) : (
                                        <a href={item.path}>{item.label}</a>
                                    )}
                                </li>
                                {index < breadcrumbItems.length - 1 && (
                                    <li className="breadcrumb-separator" aria-hidden="true">/</li>
                                )}
                            </React.Fragment>
                        ))}
                    </ol>
                </nav>
            </div>

            {/* Center: Organization Selector (Admin only) - hidden until redesign */}

            {/* Right side: Actions (User Menu) */}
            <div className="top-header-right">
                {/* Streak Counter */}
                {streakCount !== null && (
                    <button
                        type="button"
                        className={`streak-pill${streakCount > 0 ? ' streak-pill--active' : ''}`}
                        onClick={() => navigate('/my-progress')}
                        aria-label={`Current streak: ${streakCount} days`}
                    >
                        <span className="streak-flame-wrap">
                            <Flame className="streak-flame" fill="currentColor" />
                        </span>
                        <span className="streak-count">{streakCount}</span>
                        <span className="header-button-tooltip">{streakCount} day streak. Keep it going!</span>
                    </button>
                )}


                {/* Tutorial Dropdown */}
                <div className="header-menu-container">
                    <button
                        type="button"
                        ref={tutorialButtonRef}
                        className="header-button"
                        id="tour-header-tutorial"
                        onClick={() => {
                            setIsTutorialMenuOpen((prev) => {
                                const opening = !prev;
                                if (opening) {
                                    setPageTourSteps(getPageSteps(location.pathname, pageTourDriverRef));
                                }
                                return opening;
                            });
                        }}
                        aria-label="Tutorial"
                        aria-haspopup="true"
                        aria-expanded={isTutorialMenuOpen}
                    >
                        <HelpCircle />
                        <span className="header-button-tooltip">Tutorial</span>
                    </button>

                    {isTutorialMenuOpen && (
                        <div ref={tutorialMenuRef} className="header-dropdown-menu">
                            <div className="dropdown-items">
                                <button
                                    type="button"
                                    id="tour-app-tutorial-option"
                                    className="header-menu-item"
                                    onClick={() => {
                                        startReferenceTour('reference-tour', getReferenceSteps());
                                        setIsTutorialMenuOpen(false);
                                    }}
                                >
                                    App Tutorial
                                </button>
                                <button
                                    type="button"
                                    id="tour-page-tutorial-option"
                                    className="header-menu-item"
                                    disabled={pageTourSteps.length === 0}
                                    onClick={() => {
                                        pageTourDriverRef.current = startReferenceTour('page-tour', pageTourSteps);
                                        setIsTutorialMenuOpen(false);
                                    }}
                                >
                                    Page Tutorial
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Feedback Button — opens FeedbackModal directly, no dropdown */}
                <div className="header-menu-container">
                    <button
                        type="button"
                        className="header-button"
                        id="tour-header-help"
                        onClick={() => setIsFeedbackModalOpen(true)}
                        aria-label="Send Feedback"
                    >
                        <Bug />
                        <span className="header-button-tooltip">Send Feedback</span>
                    </button>
                </div>

                {/* Notification Bell */}
                <div className="header-menu-container">
                    <button
                        type="button"
                        ref={notifButtonRef}
                        className="header-button notif-bell-button"
                        id="tour-header-bell"
                        onClick={handleBellClick}
                        aria-label={bellLabel}
                        aria-haspopup="true"
                        aria-expanded={isNotifOpen}
                    >
                        <Bell />
                        {unreadCount > 0 && (
                            <span className="notif-badge" aria-hidden="true">
                                {badgeLabel}
                            </span>
                        )}
                        <span className="header-button-tooltip">Notifications</span>
                    </button>

                    {isNotifOpen && (
                        <div ref={notifRef} className="notif-dropdown" role="region" aria-label="Notifications">
                            <div className="notif-dropdown-header">
                                <span className="notif-dropdown-title">Notifications</span>
                                {unreadCount > 0 && (
                                    <button type="button" className="notif-mark-all-btn" onClick={markAllAsRead}>
                                        Mark all as read
                                    </button>
                                )}
                            </div>

                            <div className="notif-list" ref={notifListRef}>
                                {loading && (
                                    <div className="notif-empty" role="status">Loading...</div>
                                )}
                                {!loading && notifications.length === 0 && (
                                    <div className="notif-empty">No notifications yet</div>
                                )}
                                {!loading && notifications.length > 0 && (
                                    <ul className="notif-items" aria-label="Notifications">
                                        {notifications.map((notif) => (
                                            <li key={notif.id}>
                                                <button
                                                    type="button"
                                                    className={`notif-item${notif.read ? '' : ' notif-item--unread'}`}
                                                    onClick={() => handleNotifClick(notif)}
                                                >
                                                    {!notif.read && <span className="notif-unread-dot" aria-hidden="true" />}
                                                    <div className="notif-item-body">
                                                        {!notif.read && <span className="sr-only">Unread: </span>}
                                                        <p className="notif-item-message">{notif.message}</p>
                                                        <span className="notif-item-time">
                                                            {formatNotificationTime(notif.created_at)}
                                                        </span>
                                                    </div>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {loadingMore && (
                                    <div className="notif-load-more" role="status">Loading more...</div>
                                )}
                                {!loadingMore && !hasMore && notifications.length > 0 && (
                                    <div className="notif-end">You're all caught up</div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* User Dropdown */}
                <div className="header-menu-container">
                    <button
                        type="button"
                        ref={buttonRef}
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        className="header-button user-button"
                        id="tour-header-user-menu"
                        aria-label="User menu"
                        aria-haspopup="true"
                        aria-expanded={isMenuOpen}
                    >
                        <CircleUser />
                        <span className="header-button-tooltip">User Menu</span>
                    </button>

                    {isMenuOpen && (
                        <div ref={menuRef} className="header-dropdown-menu">
                            <div className="dropdown-header">
                                <span className="user-name">{currentUser?.display_name || 'User'}</span>
                                <span className="user-role">
                                    {getRoleLabel(currentUser?.role)}
                                    {isProxyActive && ' (demo view)'}
                                </span>
                            </div>
                            {realRole === 'super_admin' && (
                                <DemoProxySwitcher onSwitched={() => setIsMenuOpen(false)} />
                            )}
                            <div className="dropdown-items">
                                {canSeeSubscription && (
                                    <button
                                        type="button"
                                        onClick={() => { navigate('/subscription'); setIsMenuOpen(false); }}
                                        className="header-menu-item"
                                    >
                                        <CircleUser size={15} className="menu-item-icon" />
                                        My Profile
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => { navigate('/profile'); setIsMenuOpen(false); }}
                                    className="header-menu-item"
                                >
                                    <UserCircle size={15} className="menu-item-icon" />
                                    My Profile
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { navigate('/device-setup'); setIsMenuOpen(false); }}
                                    className="header-menu-item"
                                >
                                    <Settings2 size={15} className="menu-item-icon" />
                                    Device Setup
                                </button>
                                <button
                                    type="button"
                                    onClick={async () => { await onLogout(); setIsMenuOpen(false); }}
                                    className="header-menu-item header-menu-item--logout"
                                >
                                    <LogOut size={15} className="menu-item-icon" />
                                    Log out
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Feedback Modal */}
            <FeedbackModal
                isOpen={isFeedbackModalOpen}
                onClose={() => setIsFeedbackModalOpen(false)}
                rolePlaySettings={{
                    selections: rolePlaySelections,
                    componentDetails: componentDetails
                }}
            />

            {/* Badge-unlock celebration — rendered here (in the always-mounted layout header)
                so it surfaces on whatever page the user is currently on. Shown one at a time. */}
            {badgeQueue.length > 0 && (
                <BadgeUnlockPopup
                    key={badgeQueue[0].id}
                    badgeKey={badgeQueue[0].key}
                    onDismiss={() => setBadgeQueue((prev) => prev.filter((x) => x.id !== badgeQueue[0].id))}
                />
            )}
        </header>
    );
};

export default TopHeader;